import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const StocksEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const curriculum = [
    {
      title: 'Stock Market Fundamentals',
      lessons: [
        'How stock markets work and stock exchanges',
        'Understanding stock ownership and shares',
        'Market orders, limit orders, and stop orders',
        'Reading stock quotes and ticker symbols',
        'Market capitalization and company valuation',
        'Dividends, stock splits, and corporate actions'
      ]
    },
    {
      title: 'Fundamental Analysis',
      lessons: [
        'Reading financial statements (Balance Sheet, Income Statement, Cash Flow)',
        'Key ratios: P/E, P/B, PEG, ROE, ROA',
        'Earnings per share (EPS) and earnings growth',
        'Revenue and profit margin analysis',
        'Industry and sector analysis',
        'Competitive advantages and moat analysis'
      ]
    },
    {
      title: 'Technical Analysis for Stocks',
      lessons: [
        'Chart patterns and price action',
        'Support and resistance identification',
        'Volume analysis and accumulation/distribution',
        'Moving averages and trend following',
        'Momentum indicators (RSI, MACD, Stochastics)',
        'Fibonacci levels and extensions'
      ]
    },
    {
      title: 'Stock Trading Strategies',
      lessons: [
        'Day trading vs. swing trading vs. position trading',
        'Growth stock investing',
        'Value stock investing',
        'Dividend investing for income',
        'Momentum trading strategies',
        'Pair trading and relative strength'
      ]
    },
    {
      title: 'Risk Management',
      lessons: [
        'Position sizing and portfolio allocation',
        'Stop-loss placement strategies',
        'Diversification across sectors',
        'Managing winning and losing positions',
        'Portfolio rebalancing',
        'Tax considerations in stock trading'
      ]
    }
  ];

  return (
    <motion.div className="education-page" initial="hidden" animate="visible" variants={containerVariants}>
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Stock Trading Education</h1>
          <p>Learn stock analysis, fundamental analysis, and portfolio management</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Why Learn Stock Trading?</h2>
          <p>
            Stock trading allows you to own pieces of the world's most successful companies and benefit from their growth. 
            Whether you're building long-term wealth or actively trading for income, understanding stocks is essential for 
            financial success.
          </p>
          <div className="course-stats">
            <div className="stat"><h3>Ownership</h3><p>Own Companies</p></div>
            <div className="stat"><h3>Dividends</h3><p>Passive Income</p></div>
            <div className="stat"><h3>Growth</h3><p>Capital Appreciation</p></div>
            <div className="stat"><h3>Flexibility</h3><p>Multiple Strategies</p></div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Stock Trading Curriculum</h2>
          <div className="modules-list">
            {curriculum.map((module, index) => (
              <motion.div key={index} className="module-card" variants={itemVariants} whileHover={{ scale: 1.02 }}>
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex}>{lesson}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="trading-approaches" variants={itemVariants}>
          <h2>Stock Trading Approaches</h2>
          <div className="approaches-grid">
            <div className="approach">
              <h3>📊 Fundamental Analysis</h3>
              <p>Evaluate company financials, earnings, and intrinsic value</p>
              <ul>
                <li>Financial statement analysis</li>
                <li>Valuation ratios (P/E, P/B, PEG)</li>
                <li>Industry and competitive position</li>
              </ul>
            </div>
            <div className="approach">
              <h3>📈 Technical Analysis</h3>
              <p>Study price charts, patterns, and indicators</p>
              <ul>
                <li>Chart patterns and trends</li>
                <li>Support/resistance levels</li>
                <li>Volume and momentum indicators</li>
              </ul>
            </div>
            <div className="approach">
              <h3>💡 Growth Investing</h3>
              <p>Focus on companies with above-average growth potential</p>
              <ul>
                <li>High revenue growth rates</li>
                <li>Innovative products/services</li>
                <li>Expanding market share</li>
              </ul>
            </div>
            <div className="approach">
              <h3>💰 Value Investing</h3>
              <p>Find undervalued stocks trading below intrinsic value</p>
              <ul>
                <li>Low P/E and P/B ratios</li>
                <li>Strong fundamentals</li>
                <li>Dividend-paying companies</li>
              </ul>
            </div>
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>What Affects Stock Prices?</h2>
          <div className="factors-grid">
            <div className="factor"><h4>📈 Earnings Reports</h4><p>Quarterly results and forward guidance</p></div>
            <div className="factor"><h4>📰 Company News</h4><p>Product launches, acquisitions, management changes</p></div>
            <div className="factor"><h4>🏦 Economic Data</h4><p>GDP, employment, interest rates, inflation</p></div>
            <div className="factor"><h4>🏭 Industry Trends</h4><p>Sector developments and competition</p></div>
            <div className="factor"><h4>🌍 Market Sentiment</h4><p>Investor confidence and market psychology</p></div>
            <div className="factor"><h4>⚖️ Regulations</h4><p>Government policies and legal changes</p></div>
          </div>
        </motion.section>

        <motion.section className="success-tips" variants={itemVariants}>
          <h2>Stock Trading Success Tips</h2>
          <ul className="tips-list">
            <li>Research companies thoroughly - understand their business model</li>
            <li>Monitor earnings calendars - avoid trading right before earnings if inexperienced</li>
            <li>Diversify across sectors to reduce portfolio risk</li>
            <li>Use stop-loss orders to protect against significant losses</li>
            <li>Pay attention to volume - higher volume means better liquidity</li>
            <li>Start with blue-chip stocks before trading smaller companies</li>
            <li>Keep emotions in check - stick to your trading plan</li>
            <li>Consider both technical and fundamental factors</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Trading Stocks Successfully</h2>
          <p>Get professional stock trading education and market signals</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Stock Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Stock Signals</Link>
            <Link to="/markets/stocks" className="btn secondary">Explore Stock Markets</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default StocksEducation;
